import {
  PARTNER_TAG_ID_SPACE_ERROR,
  PartnerCustomerInventoryTag,
  PartnerCustomerInventoryTagCreateRequest,
  PartnerCustomerInventoryTagUpdateRequest,
  partnerTagIdHasSpace,
} from "../../../models/inventory/PartnerCustomerInventoryTag";
import { APIResponse } from "../../../models/NetworkResponse";

import { deleteAPI, getAPI, postAPI, putAPI } from "../../../api/Networking";

const BASE = "/v1/partner-customer-inventory-tags";

/** `tag_id` may contain characters that are not URL-safe. */
const tagPath = (customerId: number, tagId: string) =>
  `${BASE}/customer/${customerId}/tag/${encodeURIComponent(tagId)}`;

export default class PartnerCustomerInventoryTagsAPI {
  /**
   * Active tags for a customer.
   * `showLooseItems` omitted: every active tag (newest first).
   * `true` / `false`: only tags in use by unassigned loose items / kits in the master inventory.
   */
  getTagsByCustomerId = async (
    customerId: number,
    showLooseItems?: boolean,
  ): Promise<PartnerCustomerInventoryTag[]> => {
    try {
      const response = await getAPI(
        `${BASE}/customer/${customerId}`,
        showLooseItems === undefined
          ? undefined
          : { show_loose_items: String(showLooseItems) },
      );
      return (response.result ?? []) as PartnerCustomerInventoryTag[];
    } catch (error) {
      throw error;
    }
  };

  getTag = async (
    customerId: number,
    tagId: string,
  ): Promise<PartnerCustomerInventoryTag> => {
    try {
      const response = await getAPI(tagPath(customerId, tagId));
      return response.result as PartnerCustomerInventoryTag;
    } catch (error) {
      throw error;
    }
  };

  /** Returns the new DB row id. Refetch by `tag_id`, not `id`. */
  createTag = async (
    body: PartnerCustomerInventoryTagCreateRequest,
  ): Promise<{ id: number }> => {
    try {
      if (partnerTagIdHasSpace(body.tag_id)) {
        throw new Error(PARTNER_TAG_ID_SPACE_ERROR);
      }
      const response = await postAPI(BASE, body);
      return response.result as { id: number };
    } catch (error) {
      throw error;
    }
  };

  updateTag = async (
    customerId: number,
    tagId: string,
    body: PartnerCustomerInventoryTagUpdateRequest,
  ): Promise<APIResponse> => {
    try {
      return await putAPI(tagPath(customerId, tagId), body);
    } catch (error) {
      throw error;
    }
  };

  /** Soft delete for this customer only. */
  deleteTag = async (
    customerId: number,
    tagId: string,
  ): Promise<APIResponse> => {
    try {
      return await deleteAPI(tagPath(customerId, tagId));
    } catch (error) {
      throw error;
    }
  };
}
